import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { find } from 'ramda';
import moment from 'moment';
import Button from '~~elements/Button';
import Multilingual from '~~utils/Multilingual';
import { useQuery } from '~~utils/CommonUtils';
import { SectionDiv } from '../styled';
import FormalScheduleCalendar from './FormalScheduleCalendar';
import FormalScheduleLocation from './FormalScheduleLocation';
import FormalScheduleGameModal from './FormalScheduleGameModal';

const DisplayControlDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  > * {
    padding: 0.5rem 1rem;
    margin: 0 1rem 0 0;
  }
  padding-bottom: 0.2rem;
  border-bottom: 1px solid #eee;
`;

const getModalGameInitState = () => {
  return {
    uniqid: '',
    seq: 0,
    away: {
      abbr: '',
      team: 'NONE',
      team_uniqid: '',
      season_team_uniqid: '',
      icon_url: '',
      scores: [],
      runs: 0,
      hits: 0,
      errors: 0,
      box: { battings: [], fieldings: [], pitchings: [], records: [] },
    },
    home: {
      abbr: '',
      team: 'NONE',
      team_uniqid: '',
      season_team_uniqid: '',
      icon_url: '',
      scores: [],
      runs: 0,
      hits: 0,
      errors: 0,
      box: { battings: [], fieldings: [], pitchings: [], records: [] },
    },
    info: {
      location: '',
      location_uniqid: 'NONE',
      innings: 0,
      stats_completeness: 'NONE',
      stats_status: 'NORMAL',
      status: 'SCHEDULED',
      finished_status: '',
      ended_at: '',
      scheduled_start_at: '',
      started_at: '',
      winner_side: '',
      weather: '',
    },
  };
};

const FormalSchedule = ({ tournament, games }) => {
  const query = useQuery();
  const displayMode = query.get('schedule');

  const [gameLocations, setGameLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(['', '']);

  const [gameListCollapse, setGameListCollapse] = useState(false);

  const [selectedGame, setSelectedGame] = useState('');
  const [modalGame, setModalGame] = useState(getModalGameInitState());

  useEffect(() => {
    const locations = {};
    games.forEach((game) => {
      if (locations[game.info.location_uniqid]) {
        return;
      }

      locations[game.info.location_uniqid] = {
        name: game.info.location,
        uniqid: game.info.location_uniqid,
      };
    });

    setGameLocations(Object.values(locations));
  }, [games]);

  useEffect(() => {
    if (!selectedGame) {
      setModalGame(getModalGameInitState());
      return;
    }

    const targetGame = find((g) => g.uniqid === selectedGame, games);
    if (!targetGame) {
      setModalGame(getModalGameInitState());
      setSelectedGame('');
      return;
    }

    const targetGameData = { ...targetGame };
    setModalGame(targetGameData);
  }, [games, selectedGame]);

  const handleGameFilterOption = (filterType, option) => () => {
    const filters = {
      LOCATION: {
        value: selectedLocations,
        setter: setSelectedLocations,
      },
    };

    let targetFilter = filters[filterType].value.slice(0);
    switch (option) {
      case 'ALL':
        targetFilter = [];
        break;
      default: {
        const optionIndex = targetFilter.indexOf(option);
        if (optionIndex >= 0) {
          targetFilter.splice(optionIndex, 1);
          break;
        }
        targetFilter.push(option);
        break;
      }
    }

    filters[filterType].setter(targetFilter);
  };

  const handleGameFilterDateRange = (e) => {
    const name = e.target.name;
    if (name === 'all') {
      setSelectedDateRange(['', '']);
      return;
    }

    const value = new Date(e.target.value);
    if (name === 'start') {
      selectedDateRange[0] = !moment(value).isValid() ? '' : value;
    }
    if (name === 'end') {
      selectedDateRange[1] = !moment(value).isValid() ? '' : value;
    }
    setSelectedDateRange(selectedDateRange.slice(0));
  };

  const changeDisplayMode = (targetMode) => () => {
    query.set({ schedule: targetMode });
  };

  const getDisplayMode = () => {
    const commonProps = {
      tournament,
      games,
      gameLocations,
      handleGameFilterOption,
      setGameListCollapse,
      gameListCollapse,
      setSelectedGame,
    };

    switch (displayMode) {
      case 'location':
        return (
          <FormalScheduleLocation
            {...commonProps}
            handleGameFilterDateRange={handleGameFilterDateRange}
            selectedDateRange={selectedDateRange}
          />
        );
      default:
        return <FormalScheduleCalendar {...commonProps} selectedLocations={selectedLocations} />;
    }
  };

  const handleCancelGameSelect = () => {
    setModalGame(getModalGameInitState());
    setSelectedGame('');
  };

  return (
    <SectionDiv>
      <DisplayControlDiv>
        <h3>{Multilingual(`TOURNAMENT.SCHEDULE_DISPLAY_MODE`)}</h3>
        <Button
          width="unset"
          color={displayMode !== 'calendar' ? 'transparentWithBorder' : 'red'}
          nowrap
          onClick={changeDisplayMode('calendar')}
        >
          {Multilingual(`CALENDAR.WORDING`)}
        </Button>
        <Button
          width="unset"
          color={displayMode !== 'location' ? 'transparentWithBorder' : 'red'}
          nowrap
          onClick={changeDisplayMode('location')}
        >
          {Multilingual(`TOURNAMENT.LOCATION`)}
        </Button>
      </DisplayControlDiv>
      {getDisplayMode()}
      <FormalScheduleGameModal
        tournament={tournament}
        selectedGame={selectedGame}
        handleCancelGameSelect={handleCancelGameSelect}
        modalGame={modalGame}
      />
    </SectionDiv>
  );
};

export default FormalSchedule;
